import { useState, useEffect, useCallback } from 'react';
import { doctorAvailabilityAPI, doctorAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Calendar, Clock, Save, Ban, Stethoscope } from 'lucide-react';
import TouchTimePicker from '../components/TouchTimePicker';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DoctorAvailabilityPage = () => {
  const { hospital } = useAuth();
  const hospitalId = hospital?.id;
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [config, setConfig] = useState({ schedule: {}, break_start: '13:00', break_end: '14:00', slot_duration: 30, max_patients_per_slot: 1, blocked_dates: [] });
  const [blockDate, setBlockDate] = useState({ date: '', reason: '', full_day: true });
  const [saving, setSaving] = useState(false);

  const loadDoctors = useCallback(async () => {
    try {
      const res = await doctorAPI.list();
      const list = (res.data || []).filter(d => !hospitalId || d.hospital_id === hospitalId);
      setDoctors(list);
      if (list.length > 0 && !selectedDoctor) setSelectedDoctor(list[0].id);
    } catch { toast.error('Failed to load doctors'); }
  }, [hospitalId, selectedDoctor]);

  useEffect(() => { loadDoctors(); }, [loadDoctors]);

  const loadAvailability = useCallback(async () => {
    if (!selectedDoctor) return;
    try {
      const res = await doctorAvailabilityAPI.get(selectedDoctor);
      const data = res.data || {};
      const sched = data.schedule || {};
      // Initialize missing days
      DAYS.forEach(d => { if (!sched[d]) sched[d] = { available: false, start: '09:00', end: '17:00' }; });
      setConfig({
        schedule: sched,
        break_start: data.break_start || '13:00',
        break_end: data.break_end || '14:00',
        slot_duration: data.slot_duration || 30,
        max_patients_per_slot: data.max_patients_per_slot || 1,
        blocked_dates: data.blocked_dates || [],
      });
    } catch { /* not configured yet */ }
  }, [selectedDoctor]);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const updateDay = (day, field, value) => {
    setConfig({ ...config, schedule: { ...config.schedule, [day]: { ...(config.schedule[day] || {}), [field]: value } } });
  };

  const save = async () => {
    setSaving(true);
    try {
      await doctorAvailabilityAPI.set(selectedDoctor, { ...config, hospital_id: hospitalId });
      toast.success('Availability saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const addBlock = async () => {
    if (!blockDate.date) { toast.error('Pick a date'); return; }
    try {
      await doctorAvailabilityAPI.blockDate(selectedDoctor, blockDate);
      toast.success('Date blocked');
      setBlockDate({ date: '', reason: '', full_day: true });
      loadAvailability();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="doctor-availability-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Doctor Availability</h1>
          <p className="text-sm text-slate-500 mt-0.5">Weekly schedule & slot management for appointment booking</p>
        </div>
        <Button onClick={save} disabled={saving || !selectedDoctor} className="bg-teal-600 hover:bg-teal-700" data-testid="save-availability-btn">
          <Save size={16} className="mr-1.5" /> {saving ? 'Saving...' : 'Save Schedule'}
        </Button>
      </div>

      {/* Doctor Picker */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <Label>Select Doctor</Label>
        <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
          <SelectTrigger className="mt-1.5" data-testid="doctor-picker"><SelectValue placeholder="Choose doctor..." /></SelectTrigger>
          <SelectContent>
            {doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name} — {d.specialization}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selectedDoctor && (
        <>
          {/* Weekly Schedule */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Calendar size={16} /> Weekly Schedule</h3>
            <div className="space-y-3">
              {DAYS.map(day => (
                <div key={day} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50/50">
                  <div className="w-28">
                    <Switch checked={config.schedule[day]?.available || false} onCheckedChange={v => updateDay(day, 'available', v)} data-testid={`switch-${day}`} />
                    <span className="ml-2 text-sm capitalize font-medium text-slate-700">{day}</span>
                  </div>
                  <TouchTimePicker disabled={!config.schedule[day]?.available} value={config.schedule[day]?.start || '09:00'} onChange={(v) => updateDay(day, 'start', v)} className="w-32" testId={`avail-start-${day}`} />
                  <span className="text-xs text-slate-400">to</span>
                  <TouchTimePicker disabled={!config.schedule[day]?.available} value={config.schedule[day]?.end || '17:00'} onChange={(v) => updateDay(day, 'end', v)} className="w-32" testId={`avail-end-${day}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Break & Slots */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Clock size={16} /> Break Time</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start</Label><TouchTimePicker value={config.break_start} onChange={(v) => setConfig({ ...config, break_start: v })} testId="break-start" /></div>
                <div><Label>End</Label><TouchTimePicker value={config.break_end} onChange={(v) => setConfig({ ...config, break_end: v })} testId="break-end" /></div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Stethoscope size={16} /> Slot Configuration</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Duration (min)</Label><Input type="number" value={config.slot_duration} onChange={e => setConfig({ ...config, slot_duration: parseInt(e.target.value) || 30 })} /></div>
                <div><Label>Max Patients/Slot</Label><Input type="number" value={config.max_patients_per_slot} onChange={e => setConfig({ ...config, max_patients_per_slot: parseInt(e.target.value) || 1 })} /></div>
              </div>
            </div>
          </div>

          {/* Blocked Dates */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Ban size={16} /> Blocked Dates (Leave / Conferences)</h3>
            <div className="flex gap-2 items-end mb-3">
              <div className="flex-1"><Label>Date</Label><Input type="date" value={blockDate.date} onChange={e => setBlockDate({ ...blockDate, date: e.target.value })} data-testid="block-date-input" /></div>
              <div className="flex-1"><Label>Reason</Label><Input value={blockDate.reason} onChange={e => setBlockDate({ ...blockDate, reason: e.target.value })} placeholder="Conference, leave..." /></div>
              <Button onClick={addBlock} variant="outline" data-testid="add-block-btn">Add</Button>
            </div>
            <div className="space-y-1.5">
              {(config.blocked_dates || []).length === 0 ? (
                <p className="text-sm text-slate-400 italic">No blocked dates</p>
              ) : config.blocked_dates.map((b, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 bg-red-50/50 border border-red-100 rounded-lg text-sm">
                  <span className="font-medium text-red-700">{b.date}</span>
                  <span className="text-slate-500">{b.reason || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DoctorAvailabilityPage;
